import { useState, useEffect, useReducer } from 'react';
import { EntityWithId } from 'services/genericAPIGatewayService';
import { fetchObjectFromServer } from './fetchFromServer';

interface IAction<T> {
  type: 'fetchInit' | 'fetchSuccess' | 'fetchFailure' | 'fetchDone';
  payload?: {
    result?: EntityWithId<T>;
    error?: any;
  };
}

interface IState<T> {
  isLoading: boolean;
  isError: boolean;
  data: EntityWithId<T>;
  error: any;
  url?: string;
  status: 'idle' | 'pending' | 'rejected' | 'resolved';
}

const createFetchReducer = <T>() => (state: IState<T>, action: IAction<T>) => {
  switch (action.type) {
    case 'fetchInit':
      return { ...state, isLoading: true, isError: false, status: 'pending' };
    case 'fetchSuccess':
      return {
        ...state,
        isError: false,
        data: action.payload.result,
        status: 'resolved',
      };
    case 'fetchFailure':
      return {
        ...state,
        isError: true,
        error: action.payload.error,
        status: 'rejected',
      };
    case 'fetchDone':
      return {
        ...state,
        isLoading: false,
      };
    default:
      throw new Error();
  }
};

/**
 * @deprecated Use React Query instead. For more info, see here: https://bit.ly/3u0WlF7
 */
export const useFetchObjectFromServer = <T = any>(initialState?: Partial<IState<T>>) => {
  const [url, setUrl] = useState<string>(initialState?.url ?? null);

  const fetchReducer = createFetchReducer<T>();

  const [state, dispatch] = useReducer(fetchReducer, {
    isLoading: initialState && initialState.isLoading ? initialState.isLoading : false,
    isError: initialState && initialState.isError ? initialState.isError : false,
    data: initialState && initialState.data ? initialState.data : { id: null, entity: null },
    error: initialState && initialState.error ? initialState.error : null,
    status: initialState && initialState.status ? initialState.status : 'idle',
  });

  useEffect(() => {
    let didCancel = false;

    const fetchData = async () => {
      dispatch({ type: 'fetchInit' });

      fetchObjectFromServer<T>(url)
        .then(result => {
          if (!didCancel) {
            dispatch({ type: 'fetchSuccess', payload: { result } });
          }
        })
        .catch(error => {
          if (!didCancel) {
            dispatch({ type: 'fetchFailure', payload: { error } });
          }
        })
        .then(() => {
          if (!didCancel) {
            dispatch({ type: 'fetchDone' });
          }
        });
    };

    if (url) {
      fetchData();
    }

    return () => {
      didCancel = true;
    };
  }, [url]);

  return { state, setUrl };
};
